import React from "react";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import { Clear, Close, FilterAlt, Save } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { canViewStoreOwner } from "../../../api_calls/userManagement";
import ListingDataTableForModal from "../TabularLists/ListingDataTableForModal";
import Pagination from "../LoadMore/Pagination";
import { updateStateData } from "../../../Store/actions";
import {
    ListingCellCenter,
    ListingCellLeft,
    ListingHeaderContent,
} from "../TabularLists/styles";
import {
    SpaceBetweenStack,
    StyledCheckbox,
    StyledDarkRedInformationTypography,
    StyledWhiteCheckbox,
} from "../StyledComponents/styled";
import { SortIcon } from "../../../api_calls/utils";
import CloseButton from "../Buttons/CloseButton";
import ActionButton, { LightGreyButton } from "../Buttons/ActionButton";
import DisabledButton from "../Buttons/DisabledButton";
import MultiSelect from "../Selectors/MultiSelect";

const StoreDetailTable = ({
    open,
    onClose,
    storeSelectionReduxKey,
    storeOptionsList,
    applyChangesOnSelect = true,
    toHandleApplyChanges,
    selectedValues = [],
}) => {
    const storeDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();

    const isReduxManaged = applyChangesOnSelect !== false;

    const [storeNameFilter, setStoreNameFilter] = React.useState([]);
    const [locationFilter, setLocationFilter] = React.useState([]);
    const [ownerFilter, setOwnerFilter] = React.useState([]);

    const storesList =
        typeof storeOptionsList !== "undefined"
            ? storeOptionsList
            : typeof storeDetails.stores_list !== "undefined"
                ? storeDetails.stores_list
                : [];

    const [tempSelectedStoreIds, setTempSelectedStoreIds] = React.useState([]);

    const uniqueOptions = (options) => {
        const seen = new Set();

        return options.filter((option) => {
            if (seen.has(option.label)) return false;

            seen.add(option.label);
            return true;
        });
    };

    const storeOptions = storesList.map((s) => ({ id: s.id, label: s.name }));
    // Same rule as the Location filter in StoreDetailFilter: Street & No., ZIP Code, City or Region
    const locationValuesOf = (store) =>
        [store.address, store.zipCode, store.city, store.region?.name].filter(Boolean);

    const locationOptions = (() => {
        const seen = new Set();
        const options = [];

        storesList.forEach((store) => {
            locationValuesOf(store).forEach((val) => {
                if (!seen.has(val)) {
                    seen.add(val);
                    options.push({ id: val, label: val });
                }
            });
        });

        return options;
    })();
    const ownerOptions = uniqueOptions(
        storesList.map((s) => ({
            id: s.company?.id,
            label: s.company?.name,
        }))
    );

    const hasFilters =
        storeNameFilter.length || locationFilter.length || ownerFilter.length;

    const paginationStartValue =
        typeof storeDetails.store_selection_pagination !== "undefined"
            ? storeDetails.store_selection_pagination.store_list_start
            : 0;

    const paginationEndValue =
        typeof storeDetails.store_selection_pagination !== "undefined"
            ? storeDetails.store_selection_pagination.store_list_end
            : 10;

    const [sortConfig, setSortConfig] = React.useState({
        name: { dir: "asc", active: true },
        address: { dir: "asc", active: false },
        zipcode: { dir: "asc", active: false },
        city: { dir: "asc", active: false },
        region: { dir: "asc", active: false },
        owner: { dir: "asc", active: false },
        available: { dir: "asc", active: false },
    });



    const handleStoreCheckboxChange = React.useCallback((store, checked) => {
        if (store.available === false) return;

        setTempSelectedStoreIds((prev) => {
            if (checked) {
                return prev.includes(store.id) ? prev : [...prev, store.id];
            }

            return prev.filter((id) => id !== store.id);
        });
    }, []);

    const handleApply = () => {
        if (!isReduxManaged) {
            if (typeof toHandleApplyChanges === "function") {
                toHandleApplyChanges(tempSelectedStoreIds, tempSelectedStoreNames, {
                    allSelected: tempAllSelected,
                });
            }
            clearFilters();
            onClose();
            return;
        }

        dispatchAction(
            updateStateData({
                [storeSelectionReduxKey]: {
                    ...storeDetails[storeSelectionReduxKey],
                    store_ids: tempSelectedStoreIds,
                    selected_store_names: tempSelectedStoreNames,
                    all_selected: tempAllSelected,
                },
            })
        );
        clearFilters();
        onClose();
    };

    const handleCancel = () => {
        if (!isReduxManaged) {
            setTempSelectedStoreIds(Array.isArray(selectedValues) ? selectedValues : []);
            onClose();
            return;
        }

        const reduxSelection = storeDetails[storeSelectionReduxKey] || {};
        setTempSelectedStoreIds(reduxSelection.store_ids || []);
        clearFilters();
        onClose();
    };

    const applyFilters = () => {
        const filteredStores = storesList.filter((store) => {
            const matchesStoreName =
                storeNameFilter.length === 0 ||
                storeNameFilter.some((filter) => filter.id === store.id);

            const matchesLocation =
                locationFilter.length === 0 ||
                locationValuesOf(store).some((val) =>
                    locationFilter.some((filter) => filter.id === val)
                );

            const matchesOwner =
                ownerFilter.length === 0 ||
                ownerFilter.some((filter) => filter.id === store.company?.id);

            return matchesStoreName && matchesLocation && matchesOwner;
        });

        dispatchAction(
            updateStateData({
                detailed_store_list_filter_applied: true,
                detailed_stores_filtered: filteredStores,
            })
        );

        return filteredStores;
    };

    const clearFilters = () => {
        setStoreNameFilter([]);
        setLocationFilter([]);
        setOwnerFilter([]);

        dispatchAction(
            updateStateData({
                detailed_store_list_filter_applied: false,
                detailed_stores_filtered: [],
            })
        );
    };

    const sortedResults = (toBeSortedList) => {
        return toBeSortedList.slice().sort((a, b) => {
            const field = Object.keys(sortConfig).find(
                (key) => sortConfig[key].active === true
            );
            const sortDir = sortConfig[field]?.dir;

            if (field === "name") {
                return sortDir === "asc"
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name);
            }

            if (field === "address") {
                return sortDir === "asc"
                    ? a.address.localeCompare(b.address)
                    : b.address.localeCompare(a.address);
            }

            if (field === "zipcode") {
                return sortDir === "asc"
                    ? String(a.zipCode).localeCompare(String(b.zipCode))
                    : String(b.zipCode).localeCompare(String(a.zipCode));
            }

            if (field === "city") {
                return sortDir === "asc"
                    ? a.city.localeCompare(b.city)
                    : b.city.localeCompare(a.city);
            }

            if (field === "region") {
                return sortDir === "asc"
                    ? (a.region?.name || "").localeCompare(b.region?.name || "")
                    : (b.region?.name || "").localeCompare(a.region?.name || "");
            }

            if (field === "owner") {
                return sortDir === "asc"
                    ? (a.company?.name || "").localeCompare(b.company?.name || "")
                    : (b.company?.name || "").localeCompare(a.company?.name || "");
            }

            return 0;
        });
    };

    const listItems =
        typeof storeDetails.detailed_store_list_filter_applied !== "undefined" &&
            storeDetails.detailed_store_list_filter_applied === true
            ? storeDetails.detailed_stores_filtered
            : storesList;

    const storeListitems =
        listItems.length !== 0
            ? sortedResults(listItems).slice(paginationStartValue, paginationEndValue)
            : [];

    const selectableStores = React.useMemo(
        () => listItems.filter((store) => store.available !== false),
        [listItems]
    );

    const selectableStoreIds = React.useMemo(
        () => selectableStores.map((store) => store.id),
        [selectableStores]
    );

    const selectableStoreIdSet = React.useMemo(
        () => new Set(selectableStoreIds),
        [selectableStoreIds]
    );

    const selectedAvailableCount = React.useMemo(
        () => tempSelectedStoreIds.filter((id) => selectableStoreIdSet.has(id)).length,
        [tempSelectedStoreIds, selectableStoreIdSet]
    );

    const tempAllSelected =
        selectableStoreIds.length > 0 &&
        selectedAvailableCount === selectableStoreIds.length;

    const tempIndeterminate =
        selectedAvailableCount > 0 &&
        selectedAvailableCount < selectableStoreIds.length;

    const tempSelectedStoreNames = React.useMemo(() => {
        const storeById = new Map(storesList.map((store) => [store.id, store]));

        return tempSelectedStoreIds
            .map((id) => {
                const store = storeById.get(id);

                if (!store) return null;

                return {
                    label: store.name,
                    value: store.name,
                };
            })
            .filter(Boolean);
    }, [tempSelectedStoreIds, storesList]);

    const manageSelectAll = React.useCallback(
        (selectAll) => {
            setTempSelectedStoreIds(selectAll ? selectableStoreIds : []);
        },
        [selectableStoreIds]
    );

    const handleSortingParamChange = (sortField, sortDir) => {
        const sortOrder = sortDir === "asc" ? "desc" : "asc";

        setSortConfig((prev) => {
            const updated = { ...prev };

            Object.keys(updated).forEach((key) => {
                updated[key] = {
                    ...updated[key],
                    active: key === sortField,
                };
            });

            updated[sortField].dir = sortOrder;
            return updated;
        });
    };

    // Drives both the Owner column and the Owner filter so the two can never disagree
    const showOwnerColumn = canViewStoreOwner();

    const columns = React.useMemo(() => {
        const cols = [
            {
                id: "select",
                headerAlign: "center",
                align: "center",
                header: (
                    <ListingCellCenter>
                        <StyledWhiteCheckbox
                            size="small"
                            checked={tempAllSelected}
                            onChange={(e) => manageSelectAll(e.target.checked)}
                            indeterminate={tempIndeterminate}
                        />
                    </ListingCellCenter>
                ),
                render: (store) => (
                    <ListingCellCenter>
                        <StyledCheckbox
                            size="small"
                            disabled={store.available === false}
                            checked={tempSelectedStoreIds.includes(store.id)}
                            onChange={(e) =>
                                handleStoreCheckboxChange(store, e.target.checked)
                            }
                        />
                    </ListingCellCenter>
                ),
            },
            {
                id: "name",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("name", sortConfig.name.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Store Name
                        <SortIcon direction={sortConfig.name.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>{store.name}</ListingCellLeft>
                ),
            },
            {
                id: "address",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("address", sortConfig.address.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Street &amp; No.
                        <SortIcon direction={sortConfig.address.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => (
                    <ListingCellLeft>{store.address}</ListingCellLeft>
                ),
            },
            {
                id: "zipCode",
                cellSx: { width: "120px" },
                headerSx: { width: "120px" },
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("zipcode", sortConfig.zipcode.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        ZIP Code
                        <SortIcon direction={sortConfig.zipcode.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => (
                    <ListingCellLeft>{store.zipCode}</ListingCellLeft>
                ),
            },
            {
                id: "city",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("city", sortConfig.city.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        City
                        <SortIcon direction={sortConfig.city.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => (
                    <ListingCellLeft>{store.city}</ListingCellLeft>
                ),
            },
            {
                id: "region",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("region", sortConfig.region.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Region
                        <SortIcon direction={sortConfig.region.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>{store.region?.name}</ListingCellLeft>
                ),
            },
        ];

        if (showOwnerColumn) {
            cols.push({
                id: "company",
                header: (
                    <ListingHeaderContent
                        onClick={() =>
                            handleSortingParamChange("owner", sortConfig.owner.dir)
                        }
                        style={{ cursor: "pointer" }}
                    >
                        Owner
                        <SortIcon direction={sortConfig.owner.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>{store.company?.name}</ListingCellLeft>
                ),
            });
        }

        return cols;
    }, [
        sortConfig,
        tempSelectedStoreIds,
        tempAllSelected,
        tempIndeterminate,
        manageSelectAll,
        handleStoreCheckboxChange,
        showOwnerColumn,
    ]);

    React.useEffect(() => {
        if (!open) return;

        if (!isReduxManaged) {
            setTempSelectedStoreIds(Array.isArray(selectedValues) ? selectedValues : []);
            return;
        }

        const reduxSelection = storeDetails[storeSelectionReduxKey] || {};
        setTempSelectedStoreIds(reduxSelection.store_ids || []);
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h6">Detailed Store Selection</Typography>
            </DialogTitle>

            <DialogContent>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                    <MultiSelect
                        label="Store Name"
                        options={storeOptions}
                        usedFor="filter"
                        selectionCheck={(o, v) => o.id === v.id}
                        defaultVal={storeNameFilter}
                        onChangeCall={setStoreNameFilter}
                    />

                    <MultiSelect
                        label="Location"
                        options={locationOptions}
                        usedFor="filter"
                        selectionCheck={(o, v) => o.id === v.id}
                        defaultVal={locationFilter}
                        onChangeCall={setLocationFilter}
                    />

                    {/* Owner filter follows the Owner column, even when only one owner exists */}
                    {showOwnerColumn && (
                        <MultiSelect
                            label="Owner"
                            options={ownerOptions}
                            usedFor="filter"
                            selectionCheck={(o, v) => o.id === v.id}
                            defaultVal={ownerFilter}
                            onChangeCall={setOwnerFilter}
                        />
                    )}

                    <Stack direction="row" spacing={2} alignItems="center">
                        {hasFilters ? (
                            <ActionButton
                                onClick={applyFilters}
                                label="Apply"
                                icon={<FilterAlt />}
                            />
                        ) : (
                            <DisabledButton label="Apply" icon={<FilterAlt />} />
                        )}

                        {storeDetails.detailed_store_list_filter_applied ? (
                            <LightGreyButton
                                onClick={clearFilters}
                                label="Clear"
                                icon={<Clear />}
                            />
                        ) : (
                            <DisabledButton label="Clear" icon={<Clear />} />
                        )}
                    </Stack>
                </Stack>

                <Box height={25} />

                <ListingDataTableForModal
                    columns={columns}
                    rows={storeListitems}
                    rowKey={(store, idx) =>
                        `${store.number_store_cyreen ?? idx}_store_capacity`
                    }
                    emptyText="No results"
                    pagination={null}
                    onPageChange={null}
                    onPageSizeChange={null}
                />

                <Box height={25} />

                <StyledDarkRedInformationTypography variant="body2">
                    Selected {tempSelectedStoreIds.length} of {storesList.length}
                </StyledDarkRedInformationTypography>

                <SpaceBetweenStack
                    direction="row"
                    spacing={2}
                    width="100%"
                    justifyContent="space-between"
                    display="flex"
                >
                    <Pagination
                        totalCount={listItems.length}
                        reduxPaginationKeyword="store_selection_pagination"
                        reduxStartKeyword="store_list_start"
                        reduxEndKeyword="store_list_end"
                    />
                </SpaceBetweenStack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Stack direction="row" spacing={2}>
                    <CloseButton
                        onClick={handleCancel}
                        label="Cancel"
                        icon={<Close />}
                    />

                    <ActionButton
                        onClick={handleApply}
                        label="Apply"
                        icon={<Save />}
                    />
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default StoreDetailTable;